import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  CategoryNode,
  Locale,
  ProductDetail,
  ProductStatus,
  ProductSummary,
  SiteContext,
} from '@icetus/contracts';
import { LOCALES, LOCALE_LABEL } from '@icetus/contracts';
import { Badge, Button, Card } from '@icetus/ui';
import {
  bulkProductStatus,
  createCategory,
  deleteCategory,
  deleteProduct,
  fetchCategories,
  fetchProduct,
  fetchProducts,
  fetchSites,
  reorderCategories,
  translateInline,
  updateCategory,
} from '../api';
import { EditorPage, LocaleTabs, PreviewRow } from '../components/editor-page';
import { FrontLink } from '../components/site-link';
import { ImageUploader } from '../components/image-uploader';
import { MediaPickerButton } from '../components/media-picker';
import { ArtGenerator, SparkIcon } from '../components/art-generator';
import { useConfirm } from '../components/confirm';
import { Toaster, relativeTime, useToasts } from '../components/toast';
import { frontTarget } from '../lib/site-url';
import type { LoadPhase } from '../lib/load-phase';
import { ProductEditor } from './product-editor';

/**
 * 商品与分类管理。
 *
 * 布局是"左分类树、右商品表"，因为这两个操作在客户那里几乎总是一起发生：
 * 建完分类马上要往里放商品，改完商品又想看看分类别挂错了。
 *
 * 这一版重设计改了三件事，每一件都对应一个具体的操作障碍：
 *
 * 1) **分类树同时是筛选器**。原来左边是一棵只能编辑的树，右边另有一个
 *    "分类"下拉框做筛选 —— 同一件事有两个入口，两边状态还会不同步
 *    （在树上改完，右边下拉框的选项不会刷新）。现在点树上任意一个分类，
 *    右边商品表立刻筛到该分类；点"全部商品"回到全量。筛选状态只有一份。
 *
 * 2) **批量操作在选中时才出现**。原来"批量上架/批量归档"两个按钮常驻在
 *    工具条上，没选中任何东西时是灰的 —— 占着位置还让人以为是坏的。
 *    现在选中后工具条变高，多出一条明确的"已选 N 项"操作栏。
 *
 * 3) **搜索防抖**。原来每敲一个字符就打一次接口（`query` 直接进
 *    useEffect 依赖）。中文输入法下一个词会打出十几个中间态，
 *    等于为了一个查询打十几次服务器。
 *
 * 列表筛选走服务端（`?status=&categoryId=&q=`），不在前端过滤：商品上千条时
 * 前端全量拉回来会让首屏等好几秒，而分页又已经在服务端做了。
 */

type EditorState = { mode: 'new' } | { mode: 'edit'; id: string } | null;

/** 分类编辑弹窗的状态。新建时 parentId 指定层级，编辑时用 node */
type CategoryDialogState =
  | { mode: 'create'; parentId: string | null }
  | { mode: 'edit'; node: CategoryNode }
  | null;

const PAGE_SIZE = 20;

/** 搜索防抖：中文输入法下一个词会打出十几个中间态 */
const SEARCH_DEBOUNCE_MS = 320;

/**
 * 状态筛选项的显示顺序。
 *
 * 契约里的 PRODUCT_STATUSES 是按生命周期排的（草稿→上架→归档），
 * 但筛选项要按"客户最常关心的"排：先看在上架的，再看欠着的，最后看已下架的。
 */
const STATUS_FILTER_ORDER: ProductStatus[] = ['active', 'draft', 'archived'];

export function CatalogPage({
  tenantId,
  canGenerateArt,
}: {
  tenantId: string;
  /**
   * 能不能用 AI 出图（按张计费，权限同"发起翻译"：admin / owner）。
   *
   * 默认 false：漏传时宁可没有入口，也别让 editor 点进去撞 403 ——
   * 服务端认的是 `site:update`，前端这层只是别让人白点。
   */
  canGenerateArt?: boolean;
}) {
  const [sites, setSites] = useState<SiteContext[]>([]);
  const [siteId, setSiteId] = useState('');
  const [categories, setCategories] = useState<CategoryNode[]>([]);
  const [items, setItems] = useState<ProductSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<ProductStatus | ''>('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [query, setQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editor, setEditor] = useState<EditorState>(null);
  const [categoryDialog, setCategoryDialog] = useState<CategoryDialogState>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [productsPhase, setProductsPhase] = useState<LoadPhase>('loading');
  const [categoriesPhase, setCategoriesPhase] = useState<LoadPhase>('loading');
  const toasts = useToasts();
  const { confirm, confirmDialog } = useConfirm();

  /**
   * 编辑中就不渲染列表：整块让给编辑页。
   *
   * 这是"全页编辑"最关键的一步 —— 编辑页不再是浮层，而是替换掉列表本身，
   * 所以两边不会同时存在，也就不需要遮罩、焦点陷阱这些东西。
   */
  const inEditor = editor !== null || categoryDialog !== null;

  /**
   * 进出编辑页时保住滚动位置。
   *
   * 编辑时列表会被卸载（这是整页编辑的代价），回来时如果不处理，
   * 客户在第 15 个商品点编辑、返回后发现回到了列表顶部 —— 筛选条件还在，
   * 但"我刚看到哪儿"丢了，得重新滚一遍。
   */
  const listScroll = useRef(0);
  const wasInEditor = useRef(false);
  useEffect(() => {
    if (inEditor && !wasInEditor.current) {
      listScroll.current = window.scrollY;
      window.scrollTo({ top: 0 });
    } else if (!inEditor && wasInEditor.current) {
      // 等一帧：列表要先渲染出来，页面才有足够高度滚回去
      const top = listScroll.current;
      requestAnimationFrame(() => window.scrollTo({ top }));
    }
    wasInEditor.current = inEditor;
  }, [inEditor]);

  useEffect(() => {
    let cancelled = false;
    fetchSites(tenantId)
      .then((list) => {
        if (cancelled) return;
        setSites(list);
        setSiteId((prev) => (list.some((s) => s.site.id === prev) ? prev : (list[0]?.site.id ?? '')));
        // 一条站点都没有：这不是"正在加载"，是真的没东西可查。
        // 不这么标的话，两个列表会永远停在骨架态上 —— 站点永远不会来。
        if (list.length === 0) {
          setProductsPhase('ready');
          setCategoriesPhase('ready');
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        // 站点都取不到，下面两个列表必然也发起不了请求，别让骨架一直转
        setProductsPhase('error');
        setCategoriesPhase('error');
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  // 防抖：输入停顿 320ms 才真的发请求
  useEffect(() => {
    if (searchInput === query) return;
    const timer = window.setTimeout(() => {
      setQuery(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchInput, query]);

  const loadCategories = useCallback(async () => {
    // 站点还没定下来（首屏站点列表还在路上）时什么都不做，也**不能**标成 ready：
    // 那会在第一帧就把「还没有分类」写上，正是这一版要消掉的误导。
    // 真的一条站点都没有的情况由 fetchSites 那边收尾。
    if (!siteId) return;
    setCategoriesPhase('loading');
    try {
      const list = await fetchCategories(tenantId, siteId);
      setCategories(list);
      setCategoriesPhase('ready');
    } catch (err: unknown) {
      setCategoriesPhase('error');
      throw err;
    }
  }, [tenantId, siteId]);

  useEffect(() => {
    void loadCategories().catch((err: unknown) =>
      setError(err instanceof Error ? err.message : String(err)),
    );
  }, [loadCategories]);

  const loadProducts = useCallback(async () => {
    // 同 loadCategories：站点未定时保持骨架，不要抢先把空态写上
    if (!siteId) return;
    setLoading(true);
    setProductsPhase('loading');
    try {
      const result = await fetchProducts(tenantId, {
        siteId,
        status: status || undefined,
        categoryId: categoryFilter || undefined,
        q: query || undefined,
        page,
        pageSize: PAGE_SIZE,
      });
      setItems(result.items);
      setTotal(result.total);
      setProductsPhase('ready');
    } catch (err: unknown) {
      setProductsPhase('error');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [tenantId, siteId, status, categoryFilter, query, page]);

  useEffect(() => {
    void loadProducts().catch((err: unknown) =>
      setError(err instanceof Error ? err.message : String(err)),
    );
  }, [loadProducts]);

  /**
   * 换站点时先把上一站的列表清空。
   *
   * 不清的话，新站点的数据回来之前，屏幕上挂的还是别的站点的商品和分类：
   * 这时候点「删除」删的是谁、分类树上的计数属于哪一家，都说不清。
   * 所有状态塞进同一个批次里改，只触发一轮渲染、只发一次请求
   * （如果在 effect 里改 page，page 变化会让请求多发一次）。
   */
  const switchSite = (next: string) => {
    if (next === siteId) return;
    setSiteId(next);
    setPage(1);
    setItems([]);
    setTotal(0);
    setSelected(new Set());
    setProductsPhase('loading');
    setCategories([]);
    setCategoriesPhase('loading');
  };

  /** 失败后重试：先把上一轮的错误提示撤掉，否则重试成功前红条还挂着 */
  const retryProducts = () => {
    setError(null);
    void loadProducts().catch((err: unknown) =>
      setError(err instanceof Error ? err.message : String(err)),
    );
  };

  // 换站点/换筛选时清掉选中：不清理会让"批量归档"作用到看不见的行
  useEffect(() => {
    setSelected(new Set());
  }, [siteId, status, categoryFilter, query, page]);

  const [editingProduct, setEditingProduct] = useState<ProductDetail | null>(null);

  const openEditor = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      // 先确认商品存在再打开编辑器：直接传列表行会缺少图集与规格
      const detail = await fetchProduct(tenantId, id);
      setEditor({ mode: 'edit', id: detail.id });
      setEditingProduct(detail);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const afterSave = async (message: string) => {
    setEditor(null);
    setEditingProduct(null);
    toasts.push(message);
    await Promise.all([loadCategories(), loadProducts()]);
  };

  /**
   * 调整同一层里的顺序（P1-07）。
   *
   * 提交整层的 id 列表，由服务端做一次"这批 id 就是我库里这批"的核对。
   * 做上移/下移按钮而不是拖拽，是因为分类树是**嵌套**的：拖拽过程中
   * "放在 A 下面"和"放在 A 后面"只差几个像素，而客户想要的往往是后者；
   * 移动端没有可靠的拖拽手势，键盘用户更是完全用不了。
   */
  const moveCategory = async (node: CategoryNode, siblings: CategoryNode[], delta: number) => {
    const index = siblings.findIndex((n) => n.id === node.id);
    const target = index + delta;
    if (index < 0 || target < 0 || target >= siblings.length) return;

    const ids = siblings.map((n) => n.id);
    const moved = ids[index]!;
    ids[index] = ids[target]!;
    ids[target] = moved;

    setBusy(true);
    setError(null);
    try {
      const { items } = await reorderCategories(tenantId, {
        siteId,
        parentId: node.parentId,
        orderedIds: ids,
      });
      setCategories(items);
      toasts.push(`已调整「${labelOf(node)}」的顺序`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
      // 顺序冲突（409）说明手上的列表已经过期，重拉一次比让客户自己猜快
      await loadCategories();
    } finally {
      setBusy(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allSelected = items.length > 0 && items.every((item) => selected.has(item.id));

  const bulk = async (next: ProductStatus) => {
    if (selected.size === 0) return;
    setBusy(true);
    setError(null);
    try {
      const result = await bulkProductStatus(tenantId, [...selected], next);
      toasts.push(`已更新 ${result.updated} 个商品`);
      setSelected(new Set());
      await loadProducts();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (product: ProductSummary) => {
    const ok = await confirm({
      title: '删除商品？',
      message: (
        <>
          确定要删除商品「<strong>{product.name ?? product.slug}</strong>」吗？
          删除后前台与 sitemap 里都不再出现，<strong>该操作不可撤销</strong>。
        </>
      ),
      confirmText: '删除',
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await deleteProduct(tenantId, product.id);
      toasts.push('商品已删除');
      await loadProducts();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  // 默认语言决定"哪个 slug 输入框是主标识、哪些是覆盖值"，两处编辑器都要它
  const defaultLocale: Locale = sites.find((s) => s.site.id === siteId)?.tenant.defaultLocale ?? 'en';
  const currentSite = sites.find((s) => s.site.id === siteId);
  /**
   * 站点对外域名，用来拼"看前台"的地址。
   *
   * 取 primaryHost 而不是让客户自己填：这是控制台唯一知道客户站点地址的地方，
   * 而客户脑子里记的是"我们的官网"，不是 domain 表里的那条主记录。
   */
  const siteHost = currentSite?.site.primaryHost ?? '';

  /**
   * 编辑页顶栏的「看前台」。
   *
   * 放在编辑页而不是只放列表，是因为"改完想看一眼"的时刻就在保存之后——
   * 那时人还停在编辑页，回列表再找这一行纯属多余。
   * 链接指向**已保存**的内容，所以提示里要说清楚，否则客户会以为
   * "我刚才改的怎么没生效"（其实只是还没保存）。
   */
  const editingFront = editingProduct ? frontTarget(siteHost, editingProduct) : null;

  /** 当前筛中的分类名，用于工具栏上的筛选标签 */
  const activeCategory = useMemo(() => {
    if (!categoryFilter) return null;
    return flatten(categories).find((n) => n.id === categoryFilter) ?? null;
  }, [categories, categoryFilter]);

  const hasFilter = Boolean(status || categoryFilter || query);
  const clearFilters = () => {
    setStatus('');
    setCategoryFilter('');
    setSearchInput('');
    setQuery('');
    setPage(1);
  };

  return (
    <div className="catalog">
      {/* 编辑态下把列表整块收起，避免两套界面同时占着一条滚动条 */}
      {inEditor ? null : (
        <>
      {/* ── 页面头：站点 + 主操作 ─────────────────────────────── */}
      <header className="catalog__head">
        <div className="catalog__headmain">
          <label className="picker">
            <span className="picker__label">站点</span>
            <select
              className="field picker__control"
              value={siteId}
              onChange={(e) => switchSite(e.target.value)}
            >
              {sites.length === 0 ? <option value="">该租户下没有站点</option> : null}
              {sites.map((s) => (
                <option key={s.site.id} value={s.site.id}>
                  {s.site.name}
                </option>
              ))}
            </select>
          </label>
          {/*
            这里只留"这个站开了哪几种语言"。
            原来还会把 primaryHost 打出来，但它跟这一页要做的事无关（改商品），
            而域名页才是管域名的地方 —— 一行里塞两个不同用途的信息，
            客户会以为右边那串是自己的站点地址、点了能跳过去。
          */}
          {currentSite && currentSite.locales.length > 0 ? (
            <span
              className="catalog__locales"
              title={`${currentSite.site.name} 已开通 ${currentSite.locales.length} 种语言`}
            >
              已开通语言 {currentSite.locales.join(' / ')}
            </span>
          ) : null}
        </div>
        <div className="catalog__headactions">
          <Button
            variant="ghost"
            onClick={() => setCategoryDialog({ mode: 'create', parentId: null })}
            disabled={!siteId || busy}
          >
            新建分类
          </Button>
          <Button
            onClick={() => {
              setEditingProduct(null);
              setEditor({ mode: 'new' });
            }}
            disabled={!siteId || busy}
          >
            新建商品
          </Button>
        </div>
      </header>

      {error ? (
        <p className="error error--block" role="alert">
          {error}
        </p>
      ) : null}

      <div className="catalog__split">
        {/* ── 左：分类树（同时是筛选器）────────────────────── */}
        <aside className="catalog__side">
          <CategoryPanel
            siteId={siteId}
            categories={categories}
            phase={categoriesPhase}
            activeId={categoryFilter}
            onSelect={(id) => {
              setCategoryFilter(id);
              setPage(1);
            }}
            onCreate={(parentId) => setCategoryDialog({ mode: 'create', parentId })}
            onEdit={(node) => setCategoryDialog({ mode: 'edit', node })}
            onMove={(node, siblings, delta) => void moveCategory(node, siblings, delta)}
            onRetry={() => {
              setError(null);
              void loadCategories().catch((err: unknown) =>
                setError(err instanceof Error ? err.message : String(err)),
              );
            }}
            busy={busy}
          />
        </aside>

        {/* ── 右：商品表 ───────────────────────────────────── */}
        <section className="catalog__main">
          <div className="catalog__toolbar">
            <label className="catalog__search">
              <span className="visually-hidden">搜索商品</span>
              <input
                className="field"
                type="search"
                placeholder="搜索名称或标识…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </label>

            <div className="segmented" role="group" aria-label="按状态筛选">
              <button
                className={`segmented__item${status === '' ? ' is-active' : ''}`}
                onClick={() => {
                  setStatus('');
                  setPage(1);
                }}
                aria-pressed={status === ''}
              >
                全部
              </button>
              {STATUS_FILTER_ORDER.map((s) => (
                <button
                  key={s}
                  className={`segmented__item${status === s ? ' is-active' : ''}`}
                  onClick={() => {
                    setStatus(s);
                    setPage(1);
                  }}
                  aria-pressed={status === s}
                >
                  {statusLabel(s)}
                </button>
              ))}
            </div>

            <div className="catalog__toolbarmeta">
              {activeCategory ? (
                <button
                  className="chip chip--filter"
                  onClick={() => {
                    setCategoryFilter('');
                    setPage(1);
                  }}
                  title="取消该分类筛选"
                >
                  分类：{labelOf(activeCategory)}
                  <span aria-hidden="true">×</span>
                </button>
              ) : null}
              {query ? (
                <button
                  className="chip chip--filter"
                  onClick={() => {
                    setSearchInput('');
                    setQuery('');
                    setPage(1);
                  }}
                  title="清除搜索词"
                >
                  搜索：{query}
                  <span aria-hidden="true">×</span>
                </button>
              ) : null}
              {hasFilter ? (
                <Button variant="subtle" size="sm" onClick={clearFilters}>
                  清除筛选
                </Button>
              ) : null}
              <span className="muted catalog__count">
                {/*
                  首屏 total 还是 0，请求也没回来。原来这里先写「0 个商品」，
                  和下面那句「还没有商品」是同一类误导 —— 还在加载就不报数。
                */}
                {loading || productsPhase === 'loading'
                  ? '加载中…'
                  : productsPhase === 'error'
                    ? '加载失败'
                    : `${total} 个商品`}
              </span>
            </div>
          </div>

          {/* 选中时才出现的批量操作栏。列表为空或加载失败时不显示：
              那会儿批量按钮作用不到任何看得见的行 */}
          {selected.size > 0 && items.length > 0 ? (
            <div className="bulkbar" role="region" aria-label="批量操作">
              <span className="bulkbar__count">
                已选 <strong>{selected.size}</strong> 项
              </span>
              <div className="bulkbar__actions">
                <Button variant="ghost" size="sm" onClick={() => void bulk('active')} disabled={busy}>
                  批量上架
                </Button>
                <Button variant="ghost" size="sm" onClick={() => void bulk('archived')} disabled={busy}>
                  批量归档
                </Button>
                <Button
                  variant="subtle"
                  size="sm"
                  onClick={() => setSelected(new Set())}
                  disabled={busy}
                >
                  取消选择
                </Button>
              </div>
            </div>
          ) : null}

          <Card flush>
            {items.length > 0 ? (
              <div className="tablewrap">
                <table className="table table--products">
                  <thead>
                    <tr>
                      <th className="table__check">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          aria-label="全选本页商品"
                          onChange={() =>
                            setSelected(allSelected ? new Set() : new Set(items.map((i) => i.id)))
                          }
                        />
                      </th>
                      <th>商品</th>
                      <th>分类</th>
                      <th className="table__num">价格</th>
                      <th>状态</th>
                      <th>翻译</th>
                      <th>媒体 / 规格</th>
                      <th>更新</th>
                      <th className="table__actions">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const front = frontTarget(siteHost, item);
                      return (
                      <tr key={item.id} className={selected.has(item.id) ? 'is-selected' : undefined}>
                        <td className="table__check">
                          <input
                            type="checkbox"
                            checked={selected.has(item.id)}
                            aria-label={`选择 ${item.name ?? item.slug}`}
                            onChange={() => toggleSelect(item.id)}
                          />
                        </td>
                        <td>
                          <div className="pcell">
                            {item.coverUrl ? (
                              <img className="pcell__thumb" src={item.coverUrl} alt="" loading="lazy" />
                            ) : (
                              <span className="pcell__thumb pcell__thumb--empty" aria-hidden="true">
                                {item.name?.trim().charAt(0) || '?'}
                              </span>
                            )}
                            <div className="pcell__text">
                              <div className="pcell__name">{item.name ?? '（无名称）'}</div>
                              <div className="pcell__slug">/{item.slug}</div>
                            </div>
                          </div>
                        </td>
                        <td>{categoryName(categories, item.categoryId)}</td>
                        <td className="table__num">
                          {item.priceCents === null ? (
                            <span className="muted">面议</span>
                          ) : (
                            <>
                              {item.currency} {(item.priceCents / 100).toFixed(2)}
                            </>
                          )}
                        </td>
                        <td>
                          <Badge tone={statusTone(item.status)}>{statusLabel(item.status)}</Badge>
                        </td>
                        <td>
                          <TranslationCell translated={item.translatedLocales} />
                        </td>
                        <td>
                          <div className="pcell__counts">
                            <span title={`${item.imageCount} 张图片`}>图 {item.imageCount}</span>
                            <span title={`${item.variantCount} 个规格`}>规格 {item.variantCount}</span>
                          </div>
                        </td>
                        <td className="muted">{relativeTime(item.updatedAt)}</td>
                        <td className="table__actions">
                          <div className="rowactions">
                            <Button
                              variant="subtle"
                              size="sm"
                              onClick={() => void openEditor(item.id)}
                              disabled={busy}
                            >
                              编辑
                            </Button>
                            <FrontLink
                              href={front.href}
                              ariaLabel={`在新窗口打开「${item.name ?? item.slug}」的前台页面`}
                              unavailableHint={front.hint}
                            />
                            <Button
                              variant="subtle"
                              size="sm"
                              onClick={() => void remove(item)}
                              disabled={busy}
                            >
                              删除
                            </Button>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : productsPhase === 'loading' ? (
              /*
               * 加载期间铺骨架行，而不是复用空态。
               * 「还没有商品」和「还在请求」在数据上长得一模一样（都是零条），
               * 但说给客户的意思完全相反 —— 前者是「你的库是空的」，
               * 后者是「稍等，正在来」。慢网下先闪一句「还没有商品」，
               * 是最容易让人以为数据被删掉的一种错觉。
               */
              <ProductTableSkeleton />
            ) : productsPhase === 'error' ? (
              <div className="empty">
                <p className="empty__title">商品没能加载出来</p>
                <p className="muted">
                  网络不稳或服务暂时不可用，和你的商品数据无关。重新加载一次通常就好了。
                </p>
                <Button variant="ghost" onClick={retryProducts} disabled={loading}>
                  {loading ? '正在重新加载…' : '重新加载'}
                </Button>
              </div>
            ) : hasFilter ? (
              <div className="empty">
                <p className="empty__title">没有符合条件的商品</p>
                <p className="muted">试试放宽状态、换个分类，或清除搜索词。</p>
                <Button variant="ghost" onClick={clearFilters}>
                  清除筛选
                </Button>
              </div>
            ) : (
              <div className="empty">
                <p className="empty__title">还没有商品</p>
                <p className="muted">
                  先建一个分类，再新建商品挂进去。只有「已上架」的商品会出现在前台与 sitemap 里。
                </p>
                <Button
                  onClick={() => {
                    setEditingProduct(null);
                    setEditor({ mode: 'new' });
                  }}
                  disabled={!siteId}
                >
                  新建第一个商品
                </Button>
              </div>
            )}

            {/* 失败时不显示分页：那几行数字是上一轮的结果，留着会让人以为
                当前页还有内容，只是没渲染出来 */}
            {totalPages > 1 && productsPhase !== 'error' ? (
              <div className="pager">
                <span className="muted">
                  第 {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} 条，共 {total} 条
                </span>
                <div className="pager__nav">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    上一页
                  </Button>
                  <span className="muted pager__pos">
                    {page} / {totalPages}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    下一页
                  </Button>
                </div>
              </div>
            ) : null}
          </Card>
        </section>
      </div>
        </>
      )}

      {/* 商品编辑器：整页编辑（替换列表，不是盖在列表上） */}
      <ProductEditor
        open={editor !== null}
        tenantId={tenantId}
        siteId={siteId}
        defaultLocale={defaultLocale}
        product={editingProduct}
        categories={categories}
        previewHref={editingFront?.href ?? null}
        previewHint={
          editingFront
            ? editingFront.href
              ? '显示的是已保存到服务器的内容'
              : editingFront.hint
            : undefined
        }
        canGenerateArt={canGenerateArt === true}
        onCancel={() => {
          setEditor(null);
          setEditingProduct(null);
        }}
        onSaved={(saved) => {
          void afterSave(`已保存「${saved.name ?? saved.slug}」`);
        }}
      />

      {/* 分类编辑器：同一套整页编辑，表单比商品短，因此右侧预览占了更多视觉重量 */}
      <CategoryDialog
        dialog={categoryDialog}
        tenantId={tenantId}
        siteId={siteId}
        defaultLocale={defaultLocale}
        categories={categories}
        canGenerateArt={canGenerateArt === true}
        onClose={() => setCategoryDialog(null)}
        onChanged={async (message) => {
          setCategoryDialog(null);
          toasts.push(message);
          await loadCategories();
          await loadProducts();
        }}
      />

      <Toaster items={toasts.items} onDismiss={toasts.dismiss} />
      {confirmDialog}
    </div>
  );
}

// ── 分类面板 ────────────────────────────────────────────────────────

interface CategoryDraft {
  id?: string;
  parentId: string;
  slug: string;
  names: Record<string, string>;
  descriptions: Record<string, string>;
  /** 每门语言的 URL 片段覆盖值；空串表示跟随主 slug。默认语言不放进来 */
  slugs: Record<string, string>;
  /** 分类封面。空串 = 没配；imageMediaId 只在从媒体库上传时才有值 */
  imageUrl: string;
  imageAlt: string;
  imageMediaId: string | null;
}

/**
 * 从"要编辑什么"生成一份草稿。
 *
 * 抽成函数是因为它要出现在三个地方：初始 state、切换分类时的重置、
 * 以及判断"有没有未保存改动"的基线。以前这段逻辑只写了一遍（在 effect 里），
 * 现在要复用，就顺手把 effect 里那份也搬出来 —— 免得两份慢慢走样。
 */
function buildCategoryDraft(
  dialog: CategoryDialogState,
  defaultLocale: Locale,
): CategoryDraft | null {
  if (!dialog) return null;

  if (dialog.mode === 'create') {
    return {
      parentId: dialog.parentId ?? '',
      slug: '',
      names: {},
      descriptions: {},
      slugs: {},
      imageUrl: '',
      imageAlt: '',
      imageMediaId: null,
    };
  }

  const node = dialog.node;
  const names: Record<string, string> = {};
  const descriptions: Record<string, string> = {};
  const slugs: Record<string, string> = {};
  for (const locale of LOCALES) {
    const entry = node.translations[locale];
    if (!entry) continue;
    names[locale] = entry.name;
    descriptions[locale] = entry.description ?? '';
    // 默认语言用自己的主 slug 输入框，不重复给一个覆盖框
    if (locale !== defaultLocale && entry.slug) slugs[locale] = entry.slug;
  }

  return {
    id: node.id,
    parentId: node.parentId ?? '',
    slug: node.slug,
    names,
    descriptions,
    slugs,
    imageUrl: node.imageUrl ?? '',
    imageAlt: node.imageAlt ?? '',
    imageMediaId: node.imageMediaId ?? null,
  };
}

/**
 * 按 LOCALES 固定顺序展平，用于比对新旧草稿。
 *
 * 不能直接 JSON.stringify：`names` 这类 Record 的键序跟着客户先填哪门语言变，
 * 顺序一变字符串就不同 —— 表现是"什么都没改也弹放弃确认"。
 */
function fingerprintDraft(draft: CategoryDraft): string {
  return JSON.stringify([
    draft.id ?? '',
    draft.parentId,
    draft.slug,
    LOCALES.map((locale) => [locale, draft.names[locale] ?? '']),
    LOCALES.map((locale) => [locale, draft.descriptions[locale] ?? '']),
    LOCALES.map((locale) => [locale, draft.slugs[locale] ?? '']),
    draft.imageUrl,
    draft.imageAlt,
    draft.imageMediaId ?? '',
  ]);
}

/**
 * 商品表骨架。
 *
 * 只铺 6 行、不按 PAGE_SIZE 铺：它的作用是说明「正在来」并把区域高度撑住，
 * 不是精确预告条数。行高对齐真实行（缩略图 36px + 单元格 padding），
 * 数据回来时表格不会整块跳一下。
 */
function ProductTableSkeleton() {
  return (
    <div className="tablewrap" role="status" aria-live="polite">
      <span className="visually-hidden">正在加载商品…</span>
      <div className="skeleton-table" aria-hidden="true">
        {/* 表头也铺一条：真表有 thead，不画的话数据回来时表头突然插进来，
            整块内容往下跳 40px */}
        <div className="skeleton-row skeleton-row--head">
          <span className="skeleton" />
          <span className="skeleton" />
          <span className="skeleton" />
          <span className="skeleton" />
          <span className="skeleton" />
        </div>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div className="skeleton-row" key={i}>
            <span className="skeleton skeleton--check" />
            <span className="skeleton skeleton--thumb" />
            <span className="skeleton skeleton--text" />
            <span className="skeleton skeleton--text skeleton--short" />
            <span className="skeleton skeleton--text skeleton--tiny" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * 左栏分类树。
 *
 * 它同时承担两个角色：**导航**（点节点筛商品）和**管理**（增删改）。
 * 之前这两件事是分开的——树只管编辑，另一个下拉框管筛选，
 * 结果改完分类，右边下拉框里的选项还是旧的。
 *
 * 这里只做展示与跳转；真正的增删改在 CategoryDialog 里，
 * 所以本组件不碰接口，也就不需要 tenantId。
 */
function CategoryPanel({
  siteId,
  categories,
  phase,
  activeId,
  busy,
  onSelect,
  onCreate,
  onEdit,
  onMove,
  onRetry,
}: {
  siteId: string;
  categories: CategoryNode[];
  phase: LoadPhase;
  /** 当前筛选中的分类 id */
  activeId: string;
  busy: boolean;
  onSelect: (id: string) => void;
  onCreate: (parentId: string | null) => void;
  onEdit: (node: CategoryNode) => void;
  /** delta = -1 上移，+1 下移；siblings 是同一层（同父级）的完整列表 */
  onMove: (node: CategoryNode, siblings: CategoryNode[], delta: number) => void;
  onRetry: () => void;
}) {
  const totalProducts = useMemo(
    () => flatten(categories).reduce((sum, node) => sum + node.productCount, 0),
    [categories],
  );

  return (
    <Card title="分类" description="点分类可筛选右侧商品。最多 5 级，删除前必须先把商品移走。">
      {phase === 'loading' ? (
        /*
         * 和商品表同一个道理：分类还没回来时不能写「还没有分类」。
         * 这里铺几条骨架条，高度接近真实树节点，数据到了不会整块跳一下。
         */
        <div className="skeleton-stack" role="status" aria-live="polite">
          <span className="visually-hidden">正在加载分类…</span>
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className="skeleton skeleton--tree" aria-hidden="true" />
          ))}
        </div>
      ) : phase === 'error' ? (
        <p className="muted">
          分类没能加载出来，和你的分类数据无关。
          <button type="button" className="linkbtn" onClick={onRetry}>
            重新加载
          </button>
        </p>
      ) : categories.length === 0 ? (
        <p className="muted">还没有分类。可以先建一个，再把商品挂进去。</p>
      ) : (
        <>
          <button
            className={`treenode treenode--root${activeId === '' ? ' is-active' : ''}`}
            onClick={() => onSelect('')}
            aria-pressed={activeId === ''}
          >
            <span className="treenode__name">全部商品</span>
            <span className="treenode__count">{totalProducts}</span>
          </button>

          <CategoryTree
            nodes={categories}
            depth={0}
            render={(node, siblings) => {
              const index = siblings.findIndex((n) => n.id === node.id);
              const first = index <= 0;
              const last = index === siblings.length - 1;
              return (
                <div className={`treenode${activeId === node.id ? ' is-active' : ''}`}>
                  <button
                    className="treenode__label"
                    onClick={() => onSelect(activeId === node.id ? '' : node.id)}
                    aria-pressed={activeId === node.id}
                    title={`筛选「${labelOf(node)}」下的 ${node.productCount} 个商品`}
                  >
                    {/*
                      有封面就在树里显示缩略图。封面是在编辑弹窗里配的，
                      而弹窗只能从这个 ✎ 进 —— 树里看不到任何封面痕迹时，
                      "封面到底在哪改"就成了一个纯粹的猜谜题。
                    */}
                    {node.imageUrl ? (
                      <img className="treenode__thumb" src={node.imageUrl} alt="" loading="lazy" />
                    ) : null}
                    <span className="treenode__name">{labelOf(node)}</span>
                    <span className="treenode__count">{node.productCount}</span>
                  </button>
                  {/* 上移/下移/加子分类属于调理动作，悬停才出；编辑是入口，常驻 */}
                  <div className="treenode__ops">
                    <button
                      className="iconbtn"
                      title="上移"
                      aria-label={`把「${labelOf(node)}」上移一位`}
                      disabled={busy || first}
                      onClick={() => onMove(node, siblings, -1)}
                    >
                      ↑
                    </button>
                    <button
                      className="iconbtn"
                      title="下移"
                      aria-label={`把「${labelOf(node)}」下移一位`}
                      disabled={busy || last}
                      onClick={() => onMove(node, siblings, 1)}
                    >
                      ↓
                    </button>
                    <button
                      className="iconbtn"
                      title="新建子分类"
                      aria-label={`在「${labelOf(node)}」下新建子分类`}
                      onClick={() => onCreate(node.id)}
                    >
                      ＋
                    </button>
                  </div>
                  <button
                    className="iconbtn treenode__edit"
                    title="编辑分类（含封面）"
                    aria-label={`编辑「${labelOf(node)}」`}
                    onClick={() => onEdit(node)}
                  >
                    ✎
                  </button>
                </div>
              );
            }}
          />

          <div className="treenode__foot">
            <Button variant="ghost" size="sm" onClick={() => onCreate(null)} disabled={!siteId}>
              新建分类
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}

/**
 * 分类编辑弹窗。
 *
 * 原来是内联展开在左栏里的一个表单：4 种语言 × 3 个字段 = 12 个输入框
 * 挤在 260px 宽的侧栏里，改一个分类会把整个左栏撑到比商品表还高，
 * 而且"哪个输入框对应哪门语言"完全看不出来。
 * 改成居中对话框之后，字段能横排、能有小节标题，
 * 改错了也因为有遮罩而不会误触到背后的商品表。
 */
function CategoryDialog({
  dialog,
  tenantId,
  siteId,
  defaultLocale,
  categories,
  canGenerateArt,
  onClose,
  onChanged,
}: {
  dialog: CategoryDialogState;
  tenantId: string;
  siteId: string;
  defaultLocale: Locale;
  categories: CategoryNode[];
  /** 能不能用 AI 生成封面（按张计费，只有 admin / owner 有） */
  canGenerateArt: boolean;
  onClose: () => void;
  onChanged: (message: string) => Promise<void>;
}) {
  const [draft, setDraft] = useState<CategoryDraft | null>(null);
  /** 当前正在填的语言 */
  const [localeTab, setLocaleTab] = useState<Locale>(defaultLocale);
  const [busy, setBusy] = useState(false);
  /**
   * 保存/删除失败的原因，显示在顶部工具条里。
   *
   * 以前这个错误往父层（列表页）的横幅上报，可编辑态下列表整块不渲染 ——
   * 出错时界面上一个字都不变，客户只会以为按钮没反应。错误必须跟触发它的
   * 那个按钮在同一个视野里。
   */
  const [formError, setFormError] = useState<string | null>(null);
  /** 正在翻译的语言。用 Locale | null：同时只翻一门，按钮要知道哪一门在转 */
  const [translating, setTranslating] = useState<Locale | null>(null);
  /**
   * 上次翻译的一句话结果，就地显示在按钮旁边。
   * 带 tone 是因为成功也要说一声：译文只是填进输入框，不点保存等于白翻。
   */
  const [translateNote, setTranslateNote] = useState<{
    locale: Locale;
    tone: 'ok' | 'warn' | 'error';
    text: string;
  } | null>(null);
  const { confirm, confirmDialog } = useConfirm();
  /** 封面 AI 生成面板。它是盖在这层全页编辑之上的弹窗，草稿仍在这层 */
  const [coverGenOpen, setCoverGenOpen] = useState(false);
  // 每次打开都要重置草稿：上一次编辑到一半返回，再打开另一个分类时不能带上次的输入。
  const dialogKey = dialog
    ? dialog.mode === 'edit'
      ? `edit:${dialog.node.id}`
      : `create:${dialog.parentId ?? 'root'}`
    : '';
  const lastKey = useRef('');

  useEffect(() => {
    if (!dialog) {
      setDraft(null);
      lastKey.current = '';
      return;
    }
    if (lastKey.current === dialogKey) return;
    lastKey.current = dialogKey;
    setDraft(buildCategoryDraft(dialog, defaultLocale));
    setLocaleTab(defaultLocale);
  }, [dialog, dialogKey, defaultLocale]);

  /**
   * 未保存拦截。整页编辑没有遮罩，"点返回"是第一反应，
   * 而分类表单里最贵的是四门语言的名称与描述。
   */
  const requestClose = async (): Promise<void> => {
    const baseline = buildCategoryDraft(dialog, defaultLocale);
    const changed = draft !== null && baseline !== null && fingerprintDraft(draft) !== fingerprintDraft(baseline);
    if (!changed) {
      onClose();
      return;
    }
    const ok = await confirm({
      title: '放弃未保存的修改？',
      message: <>这个分类的改动还没保存，返回后需要重新填。</>,
      confirmText: '放弃修改',
      cancelText: '继续编辑',
      tone: 'danger',
    });
    if (ok) onClose();
  };

  /**
   * 把默认语言这一份名称与描述翻成目标语言，填进表单。
   *
   * 翻的是**表单里的当前值**（客户可能刚改过名字还没保存），结果也只填进表单，
   * 得点保存才真的生效。分类只有名称和描述两个字段，一次调用就够。
   */
  const translateInto = async (locale: Locale): Promise<void> => {
    if (!draft) return;
    const sourceName = draft.names[defaultLocale]?.trim() ?? '';
    if (!sourceName) {
      setTranslateNote({
        locale,
        tone: 'error',
        text: `「${LOCALE_LABEL[defaultLocale]}」的名称还空着，先回那一栏填好再来翻译`,
      });
      return;
    }

    const hasContent = Boolean(draft.names[locale]?.trim() || draft.descriptions[locale]?.trim());
    if (hasContent) {
      const ok = await confirm({
        title: `覆盖「${LOCALE_LABEL[locale]}」现有内容？`,
        message: '会用默认语言现在的名称与描述重新生成，覆盖这门语言里已有的文字。标识不在翻译范围内，不受影响。',
        confirmText: '覆盖翻译',
        cancelText: '取消',
      });
      if (!ok) return;
    }

    setTranslating(locale);
    setTranslateNote(null);
    try {
      const result = await translateInline(tenantId, {
        siteId,
        targetLocale: locale,
        entityKind: 'category',
        entityId: dialog?.mode === 'edit' ? dialog.node.id : null,
        name: sourceName,
        description: draft.descriptions[defaultLocale]?.trim() || null,
      });
      if (!result.name) {
        setTranslateNote({
          locale,
          tone: 'error',
          text: `没能翻出${LOCALE_LABEL[locale]}的名称，这种情况少见，再点一次通常就好`,
        });
        return;
      }
      // 逐字段写，不整块替换：标识（slug）不在翻译范围内，必须原样留着
      setDraft((prev) =>
        prev
          ? {
              ...prev,
              names: { ...prev.names, [locale]: result.name ?? '' },
              descriptions: result.description
                ? { ...prev.descriptions, [locale]: result.description }
                : prev.descriptions,
            }
          : prev,
      );
      setTranslateNote({
        locale,
        tone: result.notes.length > 0 ? 'warn' : 'ok',
        text:
          result.notes.length > 0
            ? `已翻译成${LOCALE_LABEL[locale]}，填入 ${result.chars} 字，其中 ${result.notes.length} 处建议核对。记得点保存`
            : `已翻译成${LOCALE_LABEL[locale]}，填入 ${result.chars} 字。记得点保存`,
      });
    } catch (err) {
      setTranslateNote({
        locale,
        tone: 'error',
        text: `翻译没成功：${err instanceof Error ? err.message : String(err)}`,
      });
    } finally {
      setTranslating(null);
    }
  };

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    setFormError(null);
    try {
      const translations: Record<string, { name: string; description: string | null; slug?: string | null }> = {};
      for (const locale of LOCALES) {
        const name = draft.names[locale]?.trim();
        if (!name) continue;
        const entry: { name: string; description: string | null; slug?: string | null } = {
          name,
          description: draft.descriptions[locale]?.trim() || null,
        };
        // 只给非默认语言带 slug：默认语言的地址就是主 slug，
        // 服务端也会把它强制写回 null（见 pinDefaultSlug）
        if (locale !== defaultLocale) {
          entry.slug = draft.slugs[locale]?.trim() || null;
        }
        translations[locale] = entry;
      }
      if (Object.keys(translations).length === 0) {
        setFormError('至少填一种语言的分类名称');
        return;
      }

      // 封面三项一起提交：只发 imageUrl 不发 imageMediaId 时，服务端会把它当成
      // "换成外链图"从而清掉媒体引用 —— 那正是我们要的语义，所以这里只要保证
      // 两者与实际状态一致
      const cover = {
        imageUrl: draft.imageUrl.trim() || null,
        imageAlt: draft.imageAlt.trim() || null,
        imageMediaId: draft.imageUrl.trim() ? draft.imageMediaId : null,
      };

      if (draft.id) {
        await updateCategory(tenantId, draft.id, {
          parentId: draft.parentId || null,
          slug: draft.slug,
          translations,
          ...cover,
        });
        await onChanged('分类已更新');
      } else {
        await createCategory(tenantId, siteId, {
          parentId: draft.parentId || null,
          slug: draft.slug || draft.names['en'] || Object.values(draft.names)[0] || '',
          translations,
          ...cover,
        });
        await onChanged('分类已创建');
      }
    } catch (err: unknown) {
      // 409（分类下还有商品/子分类）的 message 已经写明数量和下一步，直接显示
      setFormError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (node: CategoryNode) => {
    const ok = await confirm({
      title: '删除分类？',
      message: (
        <>
          确定要删除分类「<strong>{labelOf(node)}</strong>」吗？
          分类下如果还有商品或子分类，需要先移走。
        </>
      ),
      confirmText: '删除',
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    setFormError(null);
    try {
      await deleteCategory(tenantId, node.id);
      await onChanged('分类已删除');
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const editing = dialog?.mode === 'edit' ? dialog.node : null;

  const previewName = draft?.names[localeTab]?.trim() ?? '';
  const previewDescription = draft?.descriptions[localeTab]?.trim() ?? '';
  const previewCover = draft?.imageUrl.trim() ?? '';
  // 分类地址：默认语言不带前缀，其他语言 /{locale}/category/{slug}
  const previewSlug = (
    localeTab === defaultLocale ? (draft?.slug ?? '') : draft?.slugs[localeTab] || (draft?.slug ?? '')
  ).trim();
  const previewPath = `/${localeTab === defaultLocale ? '' : `${localeTab}/`}category/${previewSlug || '…'}`;
  const filledLocales = Object.fromEntries(
    LOCALES.map((locale) => [locale, Boolean(draft?.names[locale]?.trim())]),
  ) as Partial<Record<Locale, boolean>>;

  if (!dialog) return null;

  return (
    <>
      <EditorPage
        title={editing ? `编辑分类：${labelOf(editing)}` : '新建分类'}
        /*
          新建时不给说明：右边一栏就是这张分类卡在前台的样子，左边标签也把
          "上级分类 / 标识 / 封面 / 各语言名称"说清楚了，顶上再复述一遍等于
          让客户先读一段他马上就会看到的东西。
          编辑时留着 —— "改名后旧地址会自动跳转"是这个页面唯一还讲这件事的地方
          （标识字段下面那句同一意思的提示已经去掉，不该出现两次）。
        */
        description={
          editing
            ? '改名后旧地址会自动永久跳转到新地址，不会丢掉已收录的搜索结果。'
            : undefined
        }
        onBack={() => void requestClose()}
        narrow
        actions={
          <>
            {formError ? (
              <span className="error editor__error" title={formError} role="alert">
                {formError}
              </span>
            ) : null}
            {editing ? (
              <Button
                variant="danger"
                onClick={() => void remove(editing)}
                disabled={busy}
                title="删除该分类"
              >
                删除分类
              </Button>
            ) : null}
            <Button variant="ghost" onClick={() => void requestClose()} disabled={busy}>
              取消
            </Button>
            <Button onClick={() => void save()} disabled={busy || !draft}>
              {busy ? '保存中…' : '保存'}
            </Button>
          </>
        }
        aside={
          <div className="preview">
            <div className="preview__top">
              <span className="preview__kicker">首页分类卡（近似）</span>
            </div>

            <div className="preview__cover preview__cover--square">
              {previewCover ? (
                <img src={previewCover} alt="" />
              ) : (
                <span className="preview__coverempty">还没有封面</span>
              )}
            </div>

            <h3 className="preview__name">
              {previewName || <span className="preview__empty">（未填名称）</span>}
            </h3>
            <p className="preview__summary">
              {previewDescription || <span className="preview__empty">没有描述时只显示名称</span>}
            </p>

            <div className="preview__rows">
              <PreviewRow label="语言">{LOCALE_LABEL[localeTab]}</PreviewRow>
              <PreviewRow label="层级">
                {draft?.parentId ? categoryName(categories, draft.parentId) : '顶级分类'}
              </PreviewRow>
              <PreviewRow label="地址">
                <code>{previewPath}</code>
              </PreviewRow>
            </div>

            <div className="preview__langs">
              {LOCALES.map((locale) => (
                <span
                  key={locale}
                  className={`preview__lang${draft?.names[locale]?.trim() ? ' is-filled' : ''}`}
                >
                  {LOCALE_LABEL[locale]}
                </span>
              ))}
            </div>

            <p className="preview__note">
              没配封面也能正常使用，只是分类卡会退化成纯文字。
            </p>
          </div>
        }
      >
      {draft ? (
        <div className="form">
          <div className="form__grid">
            <label className="stack">
              <span>上级分类</span>
              <select
                className="field"
                value={draft.parentId}
                onChange={(e) => setDraft({ ...draft, parentId: e.target.value })}
              >
                <option value="">（顶级）</option>
                {flatten(categories)
                  .filter((n) => n.id !== draft.id)
                  .map((node) => (
                    <option key={node.id} value={node.id}>
                      {'— '.repeat(node.depth)}
                      {labelOf(node)}
                    </option>
                  ))}
              </select>
            </label>
            <label className="stack">
              <span>URL 标识</span>
              <input
                className="field"
                value={draft.slug}
                onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
                placeholder="cnc-machining"
              />
              {/* 与商品编辑页同一套写法：半句解释在输入框下面，不撑长标签 */}
              <span className="muted">留空则按名称生成。</span>
            </label>
          </div>

          {/*
            两个 fieldset 都挂 `group--legendline`：标题与那句解释同一行，
            且字号跟下面的字段标签一致（原来是 12px 灰字的 legend 加一整段提示，
            一节先占掉两行高度，而这两节的标题并不比字段标签更重要）。
          */}
          <fieldset className="group group--legendline">
            <legend>封面图片，不传封面则只有纯文字</legend>
            <div className="cover-pick">
              <div className="cover-pick__frame">
                {draft.imageUrl ? (
                  <img src={draft.imageUrl} alt={draft.imageAlt || '分类封面预览'} />
                ) : (
                  <span className="cover-pick__empty">暂无封面</span>
                )}
              </div>
              <div className="cover-pick__side">
                {/*
                  AI 生成排在最前：这一格最常见的现状是"手上什么都没有"。
                  上传和"从媒体库选"都要客户先有一张图，而这一颗不需要 ——
                  和站点设置里那个 Logo CTA 是同一个理由。
                */}
                {canGenerateArt ? (
                  <Button
                    variant="primary"
                    className="btn-ai"
                    disabled={busy}
                    onClick={() => setCoverGenOpen(true)}
                  >
                    <SparkIcon />
                    {'用 AI 生成'}
                  </Button>
                ) : null}
                <ImageUploader
                  tenantId={tenantId}
                  siteId={siteId}
                  multiple={false}
                  label={draft.imageUrl ? '更换封面' : '上传封面'}
                  disabled={busy}
                  onUploaded={(items) => {
                    const item = items[0];
                    if (!item) return;
                    // 上传完把 mediaId 一起记下来：将来媒体库这张图被删时，
                    // 服务端能把引用置空，而不是留一条指向空气的地址
                    setDraft({ ...draft, imageUrl: item.url, imageMediaId: item.id });
                  }}
                />
                {/* 与上传走同一条赋值：mediaId 同样要记下来。
                    这条路径以前不存在，所以"从媒体库选的图"在库里全是无源的 */}
                <MediaPickerButton
                  tenantId={tenantId}
                  siteId={siteId}
                  label="从媒体库选择"
                  disabled={busy}
                  onPick={(items) => {
                    const item = items[0];
                    if (!item) return;
                    setDraft({ ...draft, imageUrl: item.url, imageMediaId: item.id });
                  }}
                />
                {draft.imageUrl ? (
                  <Button
                    variant="danger"
                    disabled={busy}
                    onClick={() => setDraft({ ...draft, imageUrl: '', imageMediaId: null })}
                  >
                    移除封面
                  </Button>
                ) : null}
              </div>
            </div>
            <label className="stack">
              <span>封面地址（可直接粘贴外链）</span>
              <input
                className="field"
                value={draft.imageUrl}
                onChange={(e) =>
                  // 手改了地址就说明不再是媒体库那张图，引用必须断掉
                  setDraft({ ...draft, imageUrl: e.target.value, imageMediaId: null })
                }
                placeholder="https://… 或上传后自动填入"
              />
            </label>
            <label className="stack">
              <span>替代文本（可选，默认用分类名）</span>
              <input
                className="field"
                value={draft.imageAlt}
                onChange={(e) => setDraft({ ...draft, imageAlt: e.target.value })}
                placeholder="深色石材立面上的洗墙灯"
              />
            </label>
          </fieldset>

          <fieldset className="group group--legendline">
            <legend>
              各语言名称与地址
              <span className="form__hint">
                标识留空表示这门语言沿用主标识。地址形如
                <code> /es/category/mecanizado-cnc</code>。
              </span>
            </legend>
            <LocaleTabs value={localeTab} onChange={setLocaleTab} filled={filledLocales} />
            {LOCALES.filter((locale) => locale === localeTab).map((locale) => {
              const note = translateNote?.locale === locale ? translateNote : null;
              return (
              <div className="langblock" key={locale}>
                {locale === defaultLocale ? null : (
                  <div className={`translate-row${note ? ` is-${note.tone}` : ''}`}>
                    <Button
                      disabled={translating !== null || busy}
                      onClick={() => void translateInto(locale)}
                    >
                      {translating === locale ? (
                        '正在翻译…'
                      ) : (
                        <>
                          <svg
                            viewBox="0 0 16 16"
                            width="14"
                            height="14"
                            aria-hidden="true"
                            focusable="false"
                          >
                            <path
                              fill="currentColor"
                              d="M8 1.6l1.55 4.05L13.6 7.2l-4.05 1.55L8 12.8 6.45 8.75 2.4 7.2l4.05-1.55z"
                            />
                            <path
                              fill="currentColor"
                              opacity=".55"
                              d="M12.9 10.6l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z"
                            />
                          </svg>
                          翻译为{LOCALE_LABEL[locale]}
                        </>
                      )}
                    </Button>
                    <p
                      className="translate-row__note"
                      role={note?.tone === 'error' ? 'alert' : 'status'}
                    >
                      {note
                        ? note.text
                        : `按 ${LOCALE_LABEL[defaultLocale]} 的名称与描述自动生成，翻完可以再手改`}
                    </p>
                  </div>
                )}
                <label className="stack">
                  <span>{LOCALE_LABEL[locale]} 名称</span>
                  <input
                    className="field"
                    placeholder="例如 CNC Machining"
                    value={draft.names[locale] ?? ''}
                    onChange={(e) =>
                      setDraft({ ...draft, names: { ...draft.names, [locale]: e.target.value } })
                    }
                  />
                </label>
                <label className="stack">
                  <span>描述（可选）</span>
                  <input
                    className="field"
                    placeholder="显示在分类页顶部的一句说明"
                    value={draft.descriptions[locale] ?? ''}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        descriptions: { ...draft.descriptions, [locale]: e.target.value },
                      })
                    }
                  />
                </label>
                <label className="stack">
                  <span>{LOCALE_LABEL[locale]} 标识</span>
                  {locale === defaultLocale ? (
                    // 默认语言的地址就是上面那个主标识，这里只显示不给改：
                    // 两个输入框指向同一个字段，客户会以为改了但地址没变
                    <input className="field" value={draft.slug} disabled placeholder="同上方的标识" />
                  ) : (
                    <input
                      className="field"
                      value={draft.slugs[locale] ?? ''}
                      placeholder={`留空沿用「${draft.slug || '主标识'}」`}
                      onChange={(e) =>
                        setDraft({ ...draft, slugs: { ...draft.slugs, [locale]: e.target.value } })
                      }
                    />
                  )}
                </label>
              </div>
              );
            })}
          </fieldset>
        </div>
      ) : null}
      </EditorPage>
      {/*
        封面生成面板盖在编辑页之上，不替换它：客户在出图期间还要能看见
        自己填的分类名和已有封面，回来时草稿一个字都没动。
      */}
      <ArtGenerator
        open={coverGenOpen && canGenerateArt}
        onClose={() => setCoverGenOpen(false)}
        tenantId={tenantId}
        siteId={siteId}
        aspect="cover"
        defaultSubject={previewName}
        onApply={({ imageUrl, imageMediaId }) => {
          // 与上传/媒体库走同一条赋值：mediaId 不记下来，将来那张图被删时
          // 服务端没法把这条引用置空，这里会留一个指向空气的地址
          setDraft((prev) => (prev ? { ...prev, imageUrl, imageMediaId } : prev));
        }}
      />
      {confirmDialog}
    </>
  );
}

/**
 * 分类树递归渲染。
 *
 * 子层用嵌套 <ul> 而不是靠 padding 模拟：缩进由 DOM 结构表达，
 * 5 层时仍能靠键盘/读屏正确识别层级关系。
 */
function CategoryTree({
  nodes,
  depth,
  render,
}: {
  nodes: CategoryNode[];
  depth: number;
  /** 第二个参数是同一层的完整列表：上移/下移要知道自己在兄弟里排第几 */
  render: (node: CategoryNode, siblings: CategoryNode[]) => React.ReactNode;
}) {
  return (
    <ul className="tree" style={depth > 0 ? { paddingInlineStart: '16px' } : undefined}>
      {nodes.map((node) => (
        <li key={node.id}>
          {render(node, nodes)}
          {node.children.length > 0 ? (
            <CategoryTree nodes={node.children} depth={depth + 1} render={render} />
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function flatten(nodes: CategoryNode[], depth = 0): Array<CategoryNode & { depth: number }> {
  return nodes.flatMap((node) => [
    { ...node, depth },
    ...flatten(node.children, depth + 1),
  ]);
}

/** 分类的显示名：优先英文，其次任意一门已翻译的语言，最后退回 slug */
function labelOf(node: CategoryNode): string {
  return node.translations['en']?.name ?? Object.values(node.translations)[0]?.name ?? node.slug;
}

function categoryName(nodes: CategoryNode[], id: string | null): string {
  if (!id) return '未分类';
  const found = flatten(nodes).find((n) => n.id === id);
  return found ? labelOf(found) : '未分类';
}

function statusLabel(status: ProductStatus): string {
  return status === 'active' ? '已上架' : status === 'archived' ? '已归档' : '草稿';
}

/**
 * 状态用三种颜色而不是两种。
 *
 * 原来「已上架」是绿色，剩下两种都是灰色 —— 而"草稿"（还没发布）
 * 和"已归档"（主动下架）在客户眼里是完全不同的两件事：
 * 前者需要去完成，后者是刻意的选择。用同一个灰色表示它们，
 * 等于把"待办"和"已完成"画成同一个记号。
 */
function statusTone(status: ProductStatus): 'success' | 'warn' | 'neutral' {
  if (status === 'active') return 'success';
  if (status === 'draft') return 'warn';
  return 'neutral';
}

/**
 * 翻译列。
 *
 * 原来这一列显示的是**缺哪几种语言**（"简体中文、Español、العربية"），
 * 列头却叫"翻译"—— 看到一串语言名，没法判断这是"有"还是"没有"。
 * 现在改成四种语言各一个色块：实心 = 已翻译，空心 = 还没翻。
 * 一眼扫过去就知道哪些语言还欠着，鼠标悬停给出具体清单。
 */
function TranslationCell({ translated }: { translated: Locale[] }) {
  const missing = LOCALES.filter((l) => !translated.includes(l));
  const title =
    missing.length === 0
      ? '四种语言都已翻译'
      : `未翻译：${missing.map((l) => LOCALE_LABEL[l]).join('、')}`;
  return (
    <span className="langdots" title={title}>
      {LOCALES.map((locale) => (
        <span
          key={locale}
          className={`langdots__dot${translated.includes(locale) ? ' is-on' : ''}`}
          data-locale={locale.toUpperCase()}
          aria-label={`${LOCALE_LABEL[locale]}：${translated.includes(locale) ? '已翻译' : '未翻译'}`}
          role="img"
        />
      ))}
    </span>
  );
}
